/**
 * WorkflowCard — one of the "launch a workflow" tiles on the dashboard
 * home surface (pirx-3apc9). Renders an icon-tile, a title, a
 * description, and a tagline; the whole tile is a clickable button.
 *
 * Extracted from `pages/Dashboard.tsx` (pirx-ojst0.12) — the original
 * file declared WorkflowCard inline alongside the Dashboard route
 * component. Dashboard.tsx stays at `pages/` per spec; this card only
 * renders inside the dashboard surface and lives under
 * `features/dashboard/components/`.
 */

import { ArrowRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface WorkflowCardProps {
  title: string;
  description: string;
  tagline: string;
  accent: string;
  icon: LucideIcon;
  onClick: () => void;
}

export function WorkflowCard({
  title,
  description,
  tagline,
  accent,
  icon: Icon,
  onClick,
}: WorkflowCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex min-h-[188px] flex-col overflow-hidden rounded-[var(--r-lg)] border border-[var(--line)] bg-[var(--card)] text-left transition-colors hover:border-[var(--brand-line)]"
      data-test="dashboard-workflow-card"
    >
      <div className="flex flex-1 flex-col p-5">
        <div
          className="mb-4 flex size-11 items-center justify-center rounded-[var(--r-sm)] border"
          style={{
            background: `color-mix(in oklch, ${accent} 12%, white)`,
            borderColor: `color-mix(in oklch, ${accent} 26%, white)`,
            color: accent,
          }}
        >
          <Icon className="size-[22px]" aria-hidden="true" />
        </div>
        <div className="text-base font-bold tracking-[-0.01em] text-[var(--ink)]">{title}</div>
        <p className="mt-1.5 text-[13.5px] leading-relaxed text-[var(--ink-2)]">{description}</p>
      </div>
      <div className="flex items-center justify-between border-t border-[var(--line)] px-5 py-3 text-[var(--brand-ink)]">
        <span className="text-[13px] font-semibold">{tagline}</span>
        <ArrowRight className="size-[17px] transition-transform group-hover:translate-x-0.5" />
      </div>
    </button>
  );
}
